import { Injectable, Injector } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { from, Observable } from 'rxjs';

/**
 * Rendered explicitly so the widget is created on demand rather than by scanning the
 * DOM for `.cf-turnstile` elements. Forms that need one call `preload()` when they open,
 * and the challenge itself still only runs when the form is submitted.
 *
 * `onload` is not optional for us: without it Turnstile never calls back, and the promise
 * in loadScript() waits forever — which shows up as a submit button that does nothing at
 * all rather than as an error.
 */
const SCRIPT_URL =
    'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&onload=onloadTurnstileCallback';

/**
 * Bounds the wait for the script so a future change to the URL or a blocked request
 * surfaces as a failed submission rather than a dead button.
 */
const SCRIPT_TIMEOUT_MS = 15_000;

/**
 * A challenge the user never starts leaves the promise pending forever, which would
 * wedge the calling form's busy state. Turnstile's own `timeout-callback` covers an
 * expired challenge; this covers a user who simply walks away.
 */
const TOKEN_TIMEOUT_MS = 120_000;

/**
 * Names sent to Cloudflare as the widget `action`, and re-checked server side against
 * the endpoint being called. They must stay in step with the values in
 * `LiteracyPro.Aglet.Domain.Captcha.CaptchaActions`, otherwise verification rejects
 * every token as a cross-form replay.
 */
export const CaptchaAction = {
    SupportRequest: 'support_request',
    SendSignInCode: 'send_sign_in_code',
    SendCreateAccountCode: 'send_create_account_code'
} as const;

export type CaptchaAction = (typeof CaptchaAction)[keyof typeof CaptchaAction];

/**
 * Every failure this service reports. Callers need to tell a challenge that failed in the
 * browser from a failed request, and the type is the only reliable way: ApiService rethrows
 * the response body, so an HTTP failure reaches them as a plain object or string rather than
 * as an HttpErrorResponse.
 */
export class CaptchaError extends Error {
    constructor(readonly code: string) {
        super(code);
        this.name = 'CaptchaError';
    }
}

/** The slice of the Turnstile browser API this service uses. */
interface TurnstileApi {
    render(container: HTMLElement, options: Record<string, unknown>): string;
    execute(container: HTMLElement, options?: Record<string, unknown>): void;
    reset(widgetId: string): void;
}

declare global {
    // eslint-disable-next-line no-var
    var turnstile: TurnstileApi | undefined;
    // eslint-disable-next-line no-var
    var onloadTurnstileCallback: (() => void) | undefined;
}

interface PendingToken {
    resolve: (token: string) => void;
    reject: (reason: Error) => void;
    timer: ReturnType<typeof setTimeout>;
    /** What had focus when the token was requested; focus returns here after a challenge. */
    trigger: HTMLElement | null;
}

interface Widget {
    id: string;
    /** The element Turnstile renders the challenge into. */
    host: HTMLElement;
    /** Backdrop and panel around the host, shown only while a challenge is on screen. */
    dialog: HTMLElement;
    panel: HTMLElement;
    title: HTMLElement;
    description: HTMLElement;
}

/**
 * Acquires Cloudflare Turnstile tokens for the endpoints that require one.
 *
 * Widgets are rendered with `execution: 'execute'` and `appearance: 'interaction-only'`,
 * so nothing is shown unless Cloudflare decides the visitor needs to prove themselves —
 * and when it does, the challenge appears rather than the request being silently refused.
 *
 * One widget is kept per action. The action is fixed at render time rather than passed to
 * `execute()`, so a token can never carry the wrong action for the endpoint it is sent to.
 */
@Injectable({
    providedIn: 'root'
})
export class CaptchaService {
    private _script: Promise<void> | undefined;
    private readonly _widgets = new Map<CaptchaAction, Widget>();
    private readonly _pending = new Map<CaptchaAction, PendingToken>();

    /** Set only while a challenge is on screen; carries what is needed to undo the takeover. */
    private _open:
        | {
              action: CaptchaAction;
              returnFocusTo: HTMLElement | null;
              onFocusIn: (event: FocusEvent) => void;
              onKeydown: (event: KeyboardEvent) => void;
              inerted: Element[];
          }
        | undefined;

    /**
     * Transloco is resolved when a challenge is first shown rather than injected here. This
     * service sits under AuthService, so a constructor dependency would make i18n a required
     * provider for every test that touches authentication, none of which ever open a dialog.
     */
    constructor(private readonly _injector: Injector) {}

    /**
     * Runs the challenge for `action` and emits a single-use token, valid for five minutes.
     * Errors if the widget cannot be loaded or the challenge is not completed.
     */
    getToken(action: CaptchaAction): Observable<string> {
        return from(
            this.requestToken(action).catch((e: unknown) => {
                throw e instanceof CaptchaError
                    ? e
                    : new CaptchaError('captcha-error');
            })
        );
    }

    /**
     * Loads the script and renders the widget for `action` ahead of the submit, so the click
     * only has to run the challenge. Called when a protected form opens; failures are left
     * for getToken() to retry and report.
     */
    preload(action: CaptchaAction): void {
        this.loadScript()
            .then(() => {
                // The key arrives with the client during bootstrap, which tolerates a failed
                // fetch and retries later. A widget rendered without it would be cached broken.
                if (runtimeConfig.turnstileSiteKey) this.widgetFor(action);
            })
            .catch(() => undefined);
    }

    private async requestToken(action: CaptchaAction): Promise<string> {
        // Taken before the first await: callers request a token from their submit handler,
        // before change detection disables the submit button, and Chromium drops focus from a
        // button once it is disabled. By the time a challenge opens, focus is on <body>.
        const trigger =
            document.activeElement instanceof HTMLElement &&
            document.activeElement !== document.body
                ? document.activeElement
                : null;

        await this.loadScript();

        const widget = this.widgetFor(action);

        // The executor runs synchronously, so the pending entry is in place before the
        // reset and execute below can fire any callback.
        const token = new Promise<string>((resolve, reject) => {
            const previous = this._pending.get(action);
            if (previous) {
                clearTimeout(previous.timer);
                previous.reject(new CaptchaError('captcha-superseded'));
            }
            const timer = setTimeout(
                () => this.rejectPending(action, 'captcha-timeout'),
                TOKEN_TIMEOUT_MS
            );
            this._pending.set(action, { resolve, reject, timer, trigger });
        });

        // A token is single-use, so the widget holds a spent one after the first call.
        // Resetting before every execute is what makes "resend code" work.
        globalThis.turnstile!.reset(widget.id);
        globalThis.turnstile!.execute(widget.host);

        return token;
    }

    private widgetFor(action: CaptchaAction): Widget {
        const existing = this._widgets.get(action);
        if (existing) return existing;

        // Built here rather than in a component because all three callers submit over XHR and
        // the challenge has to outlive the control that triggered it.
        const dialog = document.createElement('div');
        dialog.className = 'captcha-dialog';
        dialog.setAttribute('role', 'dialog');
        dialog.setAttribute('aria-modal', 'true');

        const panel = document.createElement('div');
        panel.className = 'captcha-dialog__panel';
        // Focus lands here when the challenge opens, so a screen reader announces the dialog
        // and its description before the user reaches the widget itself.
        panel.tabIndex = -1;

        const title = document.createElement('h2');
        title.className = 'captcha-dialog__title';

        const description = document.createElement('p');
        description.className = 'captcha-dialog__description';

        const host = document.createElement('div');
        host.className = 'captcha-dialog__widget';

        panel.append(title, description, host);
        dialog.appendChild(panel);
        document.body.appendChild(dialog);

        const id = globalThis.turnstile!.render(host, {
            // Supplied by the API on the client response during bootstrap, which the app
            // initializer awaits — so it is always populated before a form can be submitted.
            sitekey: runtimeConfig.turnstileSiteKey,
            action: action,
            execution: 'execute',
            appearance: 'interaction-only',
            callback: (token: string) => this.resolvePending(action, token),
            'before-interactive-callback': () => this.setVisible(action, true),
            'after-interactive-callback': () => this.setVisible(action, false),
            'error-callback': (code: string) =>
                this.rejectPending(action, code || 'captcha-error'),
            'timeout-callback': () =>
                this.rejectPending(action, 'captcha-timeout')
        });

        const widget = { id, host, dialog, panel, title, description };
        this._widgets.set(action, widget);
        return widget;
    }

    /**
     * Turnstile leaves a solved widget on screen showing its success state, and the dialog is
     * outside Angular's control, so it has to be closed explicitly once the challenge is done —
     * otherwise it covers whatever the user is sent to next.
     */
    private setVisible(action: CaptchaAction, visible: boolean): void {
        const widget = this._widgets.get(action);
        if (!widget) return;

        if (visible) {
            this.open(action, widget);
        } else if (this._open?.action === action) {
            this.close();
        }
    }

    private open(action: CaptchaAction, widget: Widget): void {
        if (this._open) this.close();

        // Set at open time rather than at render time so a language switch between the first
        // challenge and a later one is reflected.
        const transloco = this._injector.get(TranslocoService);
        const title = transloco.translate('shared.captcha.title');
        widget.title.textContent = title;
        widget.description.textContent = transloco.translate(
            'shared.captcha.description'
        );
        widget.dialog.setAttribute('aria-label', title);

        widget.dialog.classList.add('captcha-dialog--open');

        // Nothing behind the challenge should be reachable while it is up. aria-modal alone is
        // unevenly honoured, so the rest of the page is made inert as well.
        const inerted: Element[] = [];
        Array.from(document.body.children).forEach((child) => {
            if (child === widget.dialog || child.hasAttribute('inert')) return;
            child.setAttribute('inert', '');
            inerted.push(child);
        });

        // Pull focus back if anything manages to move it outside the panel.
        const onFocusIn = (event: FocusEvent) => {
            const target = event.target as Node | null;
            if (target && !widget.dialog.contains(target)) widget.panel.focus();
        };

        // Abandoning the challenge must be possible from the keyboard; the form then reports
        // the failure the same way it would for any other unverified submission.
        const onKeydown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                event.stopPropagation();
                this.rejectPending(action, 'captcha-cancelled');
            }
        };

        document.addEventListener('focusin', onFocusIn);
        document.addEventListener('keydown', onKeydown);

        this._open = {
            action,
            returnFocusTo:
                this._pending.get(action)?.trigger ??
                (document.activeElement as HTMLElement | null),
            onFocusIn,
            onKeydown,
            inerted
        };

        widget.panel.focus();
    }

    private close(): void {
        const open = this._open;
        if (!open) return;
        this._open = undefined;

        const widget = this._widgets.get(open.action);
        widget?.dialog.classList.remove('captcha-dialog--open');

        document.removeEventListener('focusin', open.onFocusIn);
        document.removeEventListener('keydown', open.onKeydown);
        open.inerted.forEach((element) => element.removeAttribute('inert'));

        // Back to the button that triggered the challenge, so keyboard users resume where they
        // left off rather than at the top of the document. That button is usually disabled
        // while its form is busy, and only re-enabled once the rejection reaches the form, so
        // a focus that does not take is retried after that has happened.
        const target = open.returnFocusTo;
        target?.focus?.();
        if (target && document.activeElement !== target) {
            setTimeout(() => target.focus?.());
        }
    }

    private resolvePending(action: CaptchaAction, token: string): void {
        this.setVisible(action, false);
        const pending = this._pending.get(action);
        if (!pending) return;
        clearTimeout(pending.timer);
        this._pending.delete(action);
        pending.resolve(token);
    }

    private rejectPending(action: CaptchaAction, code: string): void {
        this.setVisible(action, false);
        const pending = this._pending.get(action);
        if (!pending) return;
        clearTimeout(pending.timer);
        this._pending.delete(action);
        pending.reject(new CaptchaError(code));
    }

    private loadScript(): Promise<void> {
        if (this._script) return this._script;

        this._script = new Promise<void>((resolve, reject) => {
            if (globalThis.turnstile) {
                resolve();
                return;
            }

            // Allow a later attempt to retry rather than caching the failure forever.
            const fail = (reason: string) => {
                this._script = undefined;
                reject(new CaptchaError(reason));
            };

            const timer = setTimeout(
                () => fail('captcha-script-timeout'),
                SCRIPT_TIMEOUT_MS
            );

            const script = document.createElement('script');
            script.src = SCRIPT_URL;
            script.async = true;
            script.defer = true;
            script.onerror = () => {
                clearTimeout(timer);
                fail('captcha-script-failed');
            };

            // `render=explicit` means the API is only safe to call once Turnstile invokes
            // this global; the script's load event fires earlier than that.
            globalThis.onloadTurnstileCallback = () => {
                clearTimeout(timer);
                resolve();
            };

            document.head.appendChild(script);
        });

        return this._script;
    }
}
